import { useState, useMemo, useCallback, useEffect } from "react";
import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { AnimatePresence } from "framer-motion";
import {
  List as ListIcon,
  LayoutGrid,
  Kanban,
  ClipboardList,
  Plus,
  SearchX,
} from "lucide-react";
import { toast } from "sonner";
import {
  PageHeader,
  Filter,
  useFilter,
  StateCard,
  ConfirmDialog,
} from "@/components/ui";
import { Button } from "@/components/ui/primitives";
import type { ViewOption, SortOption, SortState } from "@/components/ui";
import { useApiMutation } from "@/packages/features-shared";
import type { Id, UIAudit } from "@/packages/types";
import {
  AuditListView,
  AuditGridView,
  AuditBoardView,
  NewAuditWizard,
  getAuditFilterCategories,
  groupAuditsByStatus,
} from "@/components/audits";
import { useAudits } from "@/hooks/use-audits";
import { useAuditFilters } from "@/hooks/use-audit-filters";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useLocalStorage } from "@/hooks/use-local-storage";
import { useChatContext } from "@/stores/chat-context-store";
import { api } from "convex/_generated/api";
import { useTranslation } from "@/packages/utils";
import { useWorkspaceQuery, useWorkspaceId } from "@/hooks";
import { useEntitlements } from "@/packages/features-billing";

/* ------------------------------------------------------------------ */
/* View option icons (labels are translated inside the component)      */
/* ------------------------------------------------------------------ */

const VIEW_OPTION_ICONS: Record<
  string,
  React.ComponentType<{ size?: number }>
> = {
  list: ListIcon,
  grid: LayoutGrid,
  board: Kanban,
};

const DEFAULT_AUDIT_SORT: SortState = { field: "deadline", direction: "asc" };

/* ------------------------------------------------------------------ */
/* AuditsPage                                                          */
/* ------------------------------------------------------------------ */

function AuditsPage() {
  const { data: auditTags } = useWorkspaceQuery(
    api.audit_queries.getAllAuditTags,
    {},
  );

  const { data: auditTriggers } = useWorkspaceQuery(
    api.audit_queries.getAuditTriggers,
    {},
  );

  const [activeView, setActiveView] = useLocalStorage(
    "audits-active-view",
    "grid",
  );

  const filterCategories = useMemo(() => {
    const categories = getAuditFilterCategories(
      auditTags ?? [],
      auditTriggers ?? [],
    );
    if (activeView === "board") {
      return categories.map((group) => ({
        ...group,
        categories: group.categories?.filter((c) => c.id !== "status"),
      }));
    }
    return categories;
  }, [auditTags, auditTriggers, activeView]);

  return (
    <Filter categoryGroups={filterCategories}>
      <AuditsPageContent
        activeView={activeView}
        setActiveView={setActiveView}
      />
    </Filter>
  );
}

function AuditsPageContent({
  activeView,
  setActiveView,
}: {
  activeView: string;
  setActiveView: (v: string) => void;
}) {
  const { t } = useTranslation(["audits", "common"]);
  const { canCreateAudit: _canCreateAudit } = useEntitlements();
  const { workspaceSlug } = useParams({ strict: false });
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebouncedValue(searchValue);
  const [sortState, setSortState] = useState<SortState>(DEFAULT_AUDIT_SORT);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const navigate = useNavigate();
  const { filters: activeFilters, clearAllFilters } = useFilter();

  const workspaceId = useWorkspaceId();
  const archiveMutation = useApiMutation(api.audit_archive.archiveAudit, {
    name: "archive-audit",
    silentSuccess: true,
  });
  const unarchiveMutation = useApiMutation(api.audit_unarchive.unarchiveAudit, {
    name: "unarchive-audit",
    silentSuccess: true,
  });
  const deleteMutation = useApiMutation(api.audit_mutations.deleteAudit, {
    name: "delete-audit",
    silentSuccess: true,
  });

  // Set ambient chat context for Audits page
  const setAmbientContext = useChatContext((s) => s.setAmbientContext);
  const clearAmbientContext = useChatContext((s) => s.clearAmbientContext);

  useEffect(() => {
    setAmbientContext({ page: "audits", label: t("audits:list.title") });
    return () => clearAmbientContext();
  }, [setAmbientContext, clearAmbientContext, t]);

  const { filterAudits } = useAuditFilters({
    search: debouncedSearch || undefined,
    sortBy: sortState.field,
    sortOrder: sortState.direction,
  });
  const { audits: allAudits, isLoading } = useAudits();

  const audits = useMemo(
    () => (allAudits ? filterAudits(allAudits) : undefined),
    [allAudits, filterAudits],
  );

  const viewOptions: ViewOption[] = useMemo(
    () =>
      (["list", "grid", "board"] as const).map((id) => ({
        id,
        label: t(
          `audits:list.views.${id}` as "audits:list.views.list",
        ) as string,
        icon: VIEW_OPTION_ICONS[id] as ViewOption["icon"],
        active: id === activeView,
      })),
    [activeView, t],
  );

  const sortOptions: SortOption[] = useMemo(() => {
    const options = [
      { id: "deadline", label: t("audits:list.sort.deadline") },
      { id: "displayName", label: t("audits:list.sort.name") },
      { id: "status", label: t("audits:list.sort.status") },
      { id: "createdAt", label: t("audits:list.sort.created") },
    ];
    if (activeView === "board") return options.filter((o) => o.id !== "status");
    return options;
  }, [t, activeView]);

  const handleViewChange = (id: string) => {
    setSelectedIds(new Set());
    if (id === "board" && sortState.field === "status") {
      setSortState(DEFAULT_AUDIT_SORT);
    }
    setActiveView(id);
  };

  const handleSelectionChange = useCallback((nextSelectedIds: Set<string>) => {
    setSelectedIds(new Set(nextSelectedIds));
  }, []);

  const handleBulkDelete = useCallback(() => {
    if (selectedIds.size === 0) return;
    setShowBulkDeleteConfirm(true);
  }, [selectedIds]);

  const confirmBulkDelete = useCallback(async () => {
    const ids = Array.from(selectedIds) as Id<"audits">[];
    setIsBulkDeleting(true);
    try {
      const results = await Promise.allSettled(
        ids.map((id) =>
          deleteMutation.mutateAsync({ auditId: id, workspaceId }),
        ),
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed > 0) {
        toast.error(`${failed} audit(s) could not be deleted`);
      }
      setSelectedIds(new Set());
      setShowBulkDeleteConfirm(false);
    } finally {
      setIsBulkDeleting(false);
    }
  }, [selectedIds, deleteMutation, workspaceId]);

  const handleBulkArchive = useCallback(async () => {
    const ids = Array.from(selectedIds) as Id<"audits">[];
    const results = await Promise.allSettled(
      ids.map((id) =>
        archiveMutation.mutateAsync({ auditId: id, workspaceId }),
      ),
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed > 0) {
      toast.error(`${failed} audit(s) could not be archived`);
    } else {
      toast.success(`${ids.length} audits archived`);
    }
    setSelectedIds(new Set());
  }, [selectedIds, archiveMutation, workspaceId]);

  const handleBulkUnarchive = useCallback(async () => {
    const ids = Array.from(selectedIds) as Id<"audits">[];
    const results = await Promise.allSettled(
      ids.map((id) =>
        unarchiveMutation.mutateAsync({ auditId: id, workspaceId }),
      ),
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    if (failed > 0) {
      toast.error(`${failed} audit(s) could not be unarchived`);
    } else {
      toast.success(`${ids.length} audits unarchived`);
    }
    setSelectedIds(new Set());
  }, [selectedIds, unarchiveMutation, workspaceId]);

  // Determine smart archive/unarchive action based on selection
  const bulkArchiveAction = useMemo(() => {
    if (selectedIds.size === 0 || !audits) return null;
    const selected = audits.filter((a) => selectedIds.has(a.id));
    const archivedCount = selected.filter(
      (a) => a.status === "archived",
    ).length;
    if (archivedCount === 0) return "archive" as const;
    if (archivedCount === selected.length) return "unarchive" as const;
    return null; // mixed — show neither
  }, [selectedIds, audits]);

  const handleBulkExport = useCallback(() => {
    toast.info("Export coming soon");
  }, []);

  const handleCardClick = useCallback(
    (id: string) => {
      navigate({
        to: "/$workspaceSlug/audits/$auditId",
        params: { workspaceSlug: workspaceSlug ?? "", auditId: id },
      });
    },
    [navigate, workspaceSlug],
  );

  const groupedAudits = useMemo(() => {
    if (!audits) return new Map();
    return groupAuditsByStatus(audits);
  }, [audits]);

  const isEmpty = !isLoading && audits && audits.length === 0;
  const hasActiveFilters = activeFilters.length > 0 || searchValue.length > 0;

  const handleClearFilters = useCallback(() => {
    clearAllFilters();
    setSearchValue("");
  }, [clearAllFilters]);

  return (
    <div className="flex flex-col h-full overflow-hidden p-4 lg:p-6">
      <PageHeader
        title={t("audits:list.title")}
        viewOptions={viewOptions}
        onViewChange={handleViewChange}
        filterSlot={<Filter.Popover />}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder={t("audits:list.searchPlaceholder")}
        sortOptions={sortOptions}
        activeSort={sortState}
        onSortChange={setSortState}
        selectedCount={selectedIds.size}
        onBulkDelete={selectedIds.size > 0 ? handleBulkDelete : undefined}
        onBulkArchive={
          bulkArchiveAction === "archive" ? handleBulkArchive : undefined
        }
        onBulkUnarchive={
          bulkArchiveAction === "unarchive" ? handleBulkUnarchive : undefined
        }
        onBulkExport={selectedIds.size > 0 ? handleBulkExport : undefined}
        actionSlot={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => setIsWizardOpen(true)}
          >
            {t("audits:list.newAudit")}
          </Button>
        }
      />

      <Filter.PillBar />

      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center h-full">
            <StateCard variant="loading">
              <StateCard.Icon />
              <StateCard.Title>{t("audits:list.loading")}</StateCard.Title>
              <StateCard.Description>
                {t("audits:list.loadingDescription")}
              </StateCard.Description>
            </StateCard>
          </div>
        )}

        {/* Empty state — no results matching filters */}
        {isEmpty && hasActiveFilters && (
          <div className="flex items-center justify-center h-full">
            <StateCard variant="empty">
              <StateCard.Icon />
              <StateCard.Title>{t("audits:list.noMatch")}</StateCard.Title>
              <StateCard.Description>
                {t("audits:list.noMatchDescription")}
              </StateCard.Description>
              <StateCard.Action>
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent/90 transition-colors cursor-pointer"
                >
                  <SearchX size={14} />
                  {t("audits:list.clearFilters")}
                </button>
              </StateCard.Action>
            </StateCard>
          </div>
        )}

        {/* Empty state — truly no data */}
        {isEmpty && !hasActiveFilters && (
          <div className="flex items-center justify-center h-full">
            <StateCard variant="empty">
              <StateCard.Icon />
              <StateCard.Title>{t("audits:list.empty")}</StateCard.Title>
              <StateCard.Description>
                {t("audits:list.emptyDescription")}
              </StateCard.Description>
              <StateCard.Action>
                <button
                  type="button"
                  onClick={() => setIsWizardOpen(true)}
                  className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent/90 transition-colors cursor-pointer"
                >
                  <ClipboardList size={14} />
                  {t("audits:list.createAudit")}
                </button>
              </StateCard.Action>
            </StateCard>
          </div>
        )}

        {/* Audit views */}
        {!isLoading && !isEmpty && (
          <AnimatePresence mode="wait">
            {activeView === "list" && (
              <AuditListView
                key="list"
                groupedAudits={groupedAudits}
                onRowClick={handleCardClick}
                onSelectionChange={handleSelectionChange}
                selectedIds={selectedIds}
              />
            )}

            {activeView === "grid" && (
              <AuditGridView
                key="grid"
                audits={audits ?? []}
                onOpen={handleCardClick}
              />
            )}

            {activeView === "board" && (
              <AuditBoardView
                key="board"
                audits={audits ?? []}
                onCardClick={handleCardClick}
              />
            )}
          </AnimatePresence>
        )}
      </div>

      <NewAuditWizard
        open={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
      />

      <BulkDeleteDialog
        open={showBulkDeleteConfirm}
        selectedIds={selectedIds}
        audits={audits}
        loading={isBulkDeleting}
        onConfirm={confirmBulkDelete}
        onCancel={() => setShowBulkDeleteConfirm(false)}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Bulk delete confirmation dialog                                     */
/* ------------------------------------------------------------------ */

function BulkDeleteDialog({
  open,
  selectedIds,
  audits,
  loading,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  selectedIds: Set<string>;
  audits: UIAudit[] | undefined;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation(["audits", "common"]);
  const selectedNames = useMemo(() => {
    if (!audits) return [];
    return Array.from(selectedIds)
      .map((id) => audits.find((a) => a.id === id)?.displayName)
      .filter((name): name is string => !!name);
  }, [selectedIds, audits]);

  const maxShown = 5;

  return (
    <ConfirmDialog open={open} onClose={onCancel}>
      <ConfirmDialog.Title>
        Delete {selectedIds.size} audit{selectedIds.size !== 1 ? "s" : ""}?
      </ConfirmDialog.Title>
      <ConfirmDialog.Body>
        <p className="text-[13px] text-secondary mb-2">
          This action cannot be undone.
        </p>
        {selectedNames.length > 0 && (
          <ul className="list-disc pl-4 text-[13px] text-secondary space-y-0.5">
            {selectedNames.slice(0, maxShown).map((name) => (
              <li key={name}>{name}</li>
            ))}
            {selectedNames.length > maxShown && (
              <li className="text-muted">
                + {selectedNames.length - maxShown} more
              </li>
            )}
          </ul>
        )}
      </ConfirmDialog.Body>
      <ConfirmDialog.Actions
        onConfirm={onConfirm}
        onCancel={onCancel}
        confirmLabel={t("common:buttons.delete")}
        cancelLabel={t("common:buttons.cancel")}
        variant="danger"
        loading={loading}
      />
    </ConfirmDialog>
  );
}

/* ------------------------------------------------------------------ */
/* Route export                                                        */
/* ------------------------------------------------------------------ */

export const Route = createFileRoute("/$workspaceSlug/audits/")({
  component: AuditsPage,
});
